import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { clearAllCacheOnLogout } from '../utils/dataCache';

const AuthContext = createContext(null);

// Get the correct backend URL
const getBackendUrl = () => {
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

// Configure axios to include token in headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Track last refresh time to avoid excessive refreshes
let lastRefreshTime = 0;
const MIN_REFRESH_INTERVAL = 2 * 60 * 1000; // 2 minutes minimum between refreshes (reduced for better session maintenance)

// Track session activity for more aggressive refresh
let lastActivityTime = Date.now();

// Setup axios interceptors for automatic token handling
let isInterceptorSetup = false;
const setupAxiosInterceptor = (logoutCallback, refreshCallback) => {
  if (isInterceptorSetup) return;
  isInterceptorSetup = true;
  
  // Request interceptor - add token to all requests AND refresh session on activity
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('session_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        
        // Auto-refresh session on API activity (not on refresh calls themselves)
        const now = Date.now();
        const isRefreshCall = config.url?.includes('/auth/refresh') || config.url?.includes('/auth/me');
        if (!isRefreshCall && refreshCallback && (now - lastRefreshTime) > MIN_REFRESH_INTERVAL) {
          lastRefreshTime = now;
          // Fire and forget - don't wait for refresh
          refreshCallback().catch(() => {});
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle 401 and 403 errors
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // تجاهل أخطاء الشبكة (Network Error) - لا تسجيل خروج
      if (!error.response) {
        console.log('Network error - not logging out');
        return Promise.reject(error);
      }
      
      if (error.response?.status === 401) {
        // Only redirect if not already on login page and not a refresh call
        const isRefreshCall = error.config?.url?.includes('/auth/refresh');
        const isAuthMeCall = error.config?.url?.includes('/auth/me');
        // لا نسجل الخروج عند فشل refresh أو me فقط
        if (!window.location.pathname.includes('/login') && !isRefreshCall && !isAuthMeCall) {
          localStorage.removeItem('session_token');
          if (logoutCallback) logoutCallback();
          window.location.href = '/login';
        }
      }
      // Handle terminated employee
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || '';
        if (detail.includes('إنهاء خدماتك') || detail.includes('terminated')) {
          localStorage.removeItem('session_token');
          if (logoutCallback) logoutCallback();
          // Redirect to login with terminated message
          window.location.href = '/login?terminated=true';
        }
      }
      return Promise.reject(error);
    }
  );
};

// Session refresh interval: every 15 minutes (in milliseconds)
// Reduced from 30 minutes for more reliable session maintenance
const SESSION_REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes

// Activity check interval - refresh if user is active
const ACTIVITY_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = getBackendUrl();
  const refreshIntervalRef = useRef(null);
  const activityIntervalRef = useRef(null);
  const isInitializedRef = useRef(false);

  const handleLogout = useCallback(() => {
    setUser(null);
  }, []);

  // Refresh session to keep it alive - stable reference
  const refreshSession = useCallback(async () => {
    const token = localStorage.getItem('session_token');
    if (!token) return false;
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
      console.log('Session refreshed successfully, expires:', response.data.expires_at);
      lastRefreshTime = Date.now();
      return true;
    } catch (error) {
      // Silently fail - the interceptor will handle 401 errors
      console.log('Session refresh failed:', error.message);
      return false;
    }
  }, [BACKEND_URL]);

  // Check auth - stable function
  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
      setUser(response.data);
    } catch (error) {
      // فقط حذف التوكن في حالة 401 (غير مصرح) أو 403 (محظور)
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('session_token');
        setUser(null);
      } else {
        // خطأ شبكة أو خادم - الاحتفاظ بالتوكن
        console.log('Auth check failed (network/server error), keeping token:', error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  // Setup interceptor once on mount
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      setupAxiosInterceptor(handleLogout, refreshSession);
      checkAuth();
    }
  }, [handleLogout, refreshSession, checkAuth]);

  // Setup session refresh interval - only when user exists
  useEffect(() => {
    // Clear existing intervals
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
    if (activityIntervalRef.current) {
      clearInterval(activityIntervalRef.current);
      activityIntervalRef.current = null;
    }

    if (!user) {
      return;
    }

    // Refresh immediately on user login/page load
    refreshSession();
    
    // Set up periodic refresh (every 10 minutes)
    refreshIntervalRef.current = setInterval(() => {
      refreshSession();
    }, SESSION_REFRESH_INTERVAL);
    
    // Activity-aware refresh function
    const activityAwareRefresh = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityTime;
      const timeSinceRefresh = now - lastRefreshTime;
      
      if (timeSinceActivity < 10 * 60 * 1000 && timeSinceRefresh > ACTIVITY_CHECK_INTERVAL) {
        console.log('Activity-aware refresh triggered');
        refreshSession();
      }
    };
    
    // Set up activity check interval (every 5 minutes)
    activityIntervalRef.current = setInterval(activityAwareRefresh, ACTIVITY_CHECK_INTERVAL);
    
    // Visibility change handler
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page visible - refreshing session');
        lastActivityTime = Date.now();
        refreshSession();
      }
    };
    
    // Focus handler
    const handleFocus = () => {
      console.log('Window focused - refreshing session');
      lastActivityTime = Date.now();
      refreshSession();
    };
    
    // User interaction handler - throttled
    const handleUserInteraction = () => {
      lastActivityTime = Date.now();
      const now = Date.now();
      if ((now - lastRefreshTime) > MIN_REFRESH_INTERVAL) {
        lastRefreshTime = now;
        refreshSession();
      }
    };
    
    // Keyboard activity
    const handleKeyPress = () => {
      lastActivityTime = Date.now();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('click', handleUserInteraction, { passive: true });
    document.addEventListener('touchstart', handleUserInteraction, { passive: true });
    document.addEventListener('keydown', handleKeyPress, { passive: true });
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('keydown', handleKeyPress);
      if (activityIntervalRef.current) {
        clearInterval(activityIntervalRef.current);
      }
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [user, refreshSession]);

  const login = async (email, password) => {
    // مسح الكاش قبل تسجيل الدخول لضمان بيانات نظيفة
    clearAllCacheOnLogout();
    console.log('🗑️ Cache cleared before login');
    
    const response = await axios.post(
      `${BACKEND_URL}/api/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    // Store token in localStorage as backup
    if (response.data.session_token) {
      localStorage.setItem('session_token', response.data.session_token);
    }
    setUser(response.data.user);
    return response.data;
  };

  const register = async (data) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/register`, data);
    return response.data;
  };

  const logout = async () => {
    // Clear refresh interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { 
        withCredentials: true,
        headers: getAuthHeaders()
      });
    } catch (e) {
      // Ignore logout errors - we're logging out anyway
      console.log('Logout request failed:', e.message);
    }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
    refreshSession
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};